import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { of } from 'rxjs';
import { ImageQuality } from '@pcp/core/interfaces';
import {
    IS_PRODUCTION,
    PCP_SHARED_PREFERRED_IMAGE_QUALITY,
    PCP_SHARED_SERVICE_URL,
    PUBLIC_BLOB_URL,
} from './app-config-tokens';

/**
 * Configuration interface for app-wide settings.
 */
export interface AppConfig {
    /** Base URL for API service calls */
    serverUrl: string;
    /** Public blob storage URL for accessing public resources */
    publicBlobUrl: string;
    /** Whether the app is running in production mode */
    isProduction: boolean;
}

/**
 * Provides app-wide configuration for Angular applications.
 * This replaces the old `SharedUtilAppConfigModule.forRoot()` pattern.
 *
 * @param config - App configuration containing server URL, blob URL, and production flag
 *
 * @example
 * ```typescript
 * bootstrapApplication(AppComponent, {
 *     providers: [
 *         provideAppConfig({
 *             serverUrl: environment.service,
 *             publicBlobUrl: environment.blob_url + '/public',
 *             isProduction: environment.production,
 *         }),
 *         // ... other providers
 *     ]
 * });
 * ```
 */
export function provideAppConfig(config: AppConfig): EnvironmentProviders {
    return makeEnvironmentProviders([
        { provide: PCP_SHARED_SERVICE_URL, useValue: config.serverUrl },
        { provide: PUBLIC_BLOB_URL, useValue: config.publicBlobUrl },
        { provide: IS_PRODUCTION, useValue: config.isProduction },
        {
            provide: APP_BASE_HREF,
            useFactory: (platformLocation: PlatformLocation) =>
                platformLocation.getBaseHrefFromDOM(),
            deps: [PlatformLocation],
        },
        // NOTE: can't use PREFERRED_IMAGE_QUALITY token here, because it often needs to be resolved
        //       at runtime and is thus not possible to inject as a static value
        // ⚠️ This LOW default participates in DI like any other provider: an app's own
        //    PCP_SHARED_PREFERRED_IMAGE_QUALITY provider MUST be listed AFTER provideAppConfig(...)
        //    in its bootstrap providers, or this default silently wins (last provider for a token
        //    wins). This bit the beamer once — see `canvas-image-quality`.
        { provide: PCP_SHARED_PREFERRED_IMAGE_QUALITY, useValue: of(ImageQuality.LOW) },
    ]);
}
